/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import classnames from 'classnames';

import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import { IDlvryNavigationMenuData } from './dlvry-navigation-menu.data';
import { IDlvryNavigationMenuProps, menuItemsSource } from './dlvry-navigation-menu.props.autogenerated';

export interface IDlvryNavigationMenuViewProps extends IDlvryNavigationMenuProps<IDlvryNavigationMenuData> {
    navigationMenu: IModuleProps;
    baseClassName: string;
    productMenuItem: IMenuItem;
    menuItems: IMenuItem[];
    customerName: JSX.Element | null;
}

export interface IMenuItem {
    title: string;
    url?: string;
    children?: IMenuItem[];
    showUnauthorized: boolean;
    highlight?: boolean;
}

/**
 *
 * DlvryNavigationMenu component
 * @extends {React.PureComponent<IDlvryNavigationMenuProps<IDlvryNavigationMenuData>>}
 */
class DlvryNavigationMenu extends React.PureComponent<IDlvryNavigationMenuProps<IDlvryNavigationMenuData>> {
    private readonly moduleClassName: string = 'dlvry-navigation-menu';

    public render(): JSX.Element | null {
        const {
            config: {
                cmsNavItems,
                showProductsMenuItemOnlyAuthorized,
                showAllProductsLink,
                showAllProductsLabel
            },
            context,
            data: { categories },
            resources: { productsMenuItemLabel }
        } = this.props;

        const isAuthenticated = context.request.user.isAuthenticated;
        const menuItems: IMenuItem[] = [];
        let productMenuItemChildren: IMenuItem[] | undefined;

        if (this.props.config.menuItemsSource === menuItemsSource.static) {
            cmsNavItems?.filter(x => !x.showOnlyAuthorized || isAuthenticated).forEach(navItem => {
                if (!navItem.linkText || !navItem.linkUrl) {
                    return;
                }

                menuItems.push({
                    title: navItem.linkText,
                    url: navItem.linkUrl.destinationUrl,
                    showUnauthorized: !navItem.showOnlyAuthorized,
                    highlight: navItem.highlight,
                    children: navItem.subMenus?.map(subMenu => ({
                        title: subMenu.linkText,
                        url: subMenu.linkUrl.destinationUrl,
                        showUnauthorized: !subMenu.showOnlyAuthorized
                    }))
                });
            });

            productMenuItemChildren = categories.result
                ?.sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
                ?.map(category => ({
                    title: category.Name,
                    url: category.Url
                }) as IMenuItem);

            if (showAllProductsLink && showAllProductsLink.destinationUrl) {
                productMenuItemChildren?.unshift(
                    {
                        title: showAllProductsLabel || 'All products',
                        url: showAllProductsLink.destinationUrl,
                        showUnauthorized: false
                    },
                    {
                        title: '<SEPARATOR>',
                        showUnauthorized: false
                    }
                );
            }
        } else {
            categories.result
                ?.sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
                ?.forEach(category => {
                    menuItems.push({
                        title: category.Name || 'Missing category name',
                        url: category.Url,
                        showUnauthorized: false
                    });
                });

            if (showAllProductsLink && showAllProductsLink.destinationUrl) {
                menuItems?.unshift(
                    {
                        title: showAllProductsLabel || 'All products',
                        url: showAllProductsLink.destinationUrl,
                        showUnauthorized: false
                    }
                );
            }
        }

        const viewProps: IDlvryNavigationMenuViewProps = {
            ...this.props,
            navigationMenu: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, this.props.config.className)
            },
            baseClassName: this.moduleClassName,
            productMenuItem: {
                title: productsMenuItemLabel,
                children: productMenuItemChildren,
                showUnauthorized: !showProductsMenuItemOnlyAuthorized
            },
            menuItems,
            customerName: this.renderCustomerName()
        };

        return this.props.renderView(viewProps);
    }

    private renderCustomerName(): JSX.Element | null {
        const { context, data } = this.props;

        if (!context.request.user.isAuthenticated || !context.request.user.isOBORequest) {
            return null;
        }

        const orgName = data.organizationInformation.result?.Name || '';
        const orgNumber = data.organizationInformation.result?.AccountNumber || '';

        return (
            <div className={`${this.moduleClassName}__customer-name`}>
                <div>
                    {orgName}
                </div>
                <div>
                    {orgNumber}
                </div>
            </div>
        );
    }
}

export default DlvryNavigationMenu;
